(() => {
  // Session Recommender (TKT-0078) browser history: products this shopper
  // viewed, clicked, or removed from the bag. It never leaves the browser except
  // as product IDs in the product-page recommendation request.
  const STORAGE_KEY = 'wob:session-history:v1';
  const CART_KEY = 'wob:session-history:cart:v1';
  const MAX_ENTRIES = 24;
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const SIGNALS = new Set(['viewed', 'clicked', 'removed']);

  const numericId = (value) => {
    const match = String(value ?? '').trim().match(/(\d+)$/);
    return match ? match[1] : '';
  };

  // Personalization is a preferences purpose. Record nothing until Shopify's
  // privacy API says preferences processing is allowed.
  const allowed = () => window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;

  const read = (storage, key) => {
    try {
      const value = JSON.parse(storage.getItem(key) || 'null');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const write = (storage, key, value) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Storage can be full or blocked; history is best-effort.
    }
  };

  const clear = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(CART_KEY);
    } catch (error) {
      // Nothing stored.
    }
  };

  const list = () => {
    if (!allowed()) return [];
    const cutoff = Date.now() - MAX_AGE_MS;
    return read(window.localStorage, STORAGE_KEY)
      .filter((entry) => entry && numericId(entry.productId) && SIGNALS.has(entry.signal) && Number(entry.at) >= cutoff)
      .slice(0, MAX_ENTRIES);
  };

  const record = (productId, signal) => {
    const id = numericId(productId);
    if (!id || !SIGNALS.has(signal) || !allowed()) return;
    const entries = list().filter((entry) => !(entry.productId === id && entry.signal === signal));
    entries.unshift({ productId: id, signal, at: Date.now() });
    write(window.localStorage, STORAGE_KEY, entries.slice(0, MAX_ENTRIES));
  };

  const cartProductIdsFromDom = () => Array.from(document.querySelectorAll('[data-cart-drawer-item][data-product-id]'))
    .map((item) => numericId(item.dataset.productId))
    .filter(Boolean);

  const cartProductIds = () => {
    const stored = read(window.sessionStorage, CART_KEY).map(numericId).filter(Boolean);
    return Array.from(new Set(stored.length ? stored : cartProductIdsFromDom()));
  };

  const rememberCart = (ids) => write(window.sessionStorage, CART_KEY, Array.from(new Set(ids)));

  document.addEventListener('cart:updated', (event) => {
    const items = event.detail?.cart?.items;
    if (!Array.isArray(items)) return;
    const previous = cartProductIds();
    const current = items.map((item) => numericId(item.product_id)).filter(Boolean);
    previous.filter((id) => !current.includes(id)).forEach((id) => record(id, 'removed'));
    rememberCart(current);
  });

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-product-card-link]') : null;
    const card = link?.closest('.product-card[data-product-id]');
    if (card) record(card.dataset.productId, 'clicked');
  }, true);

  const consentChanged = () => {
    if (window.Shopify?.customerPrivacy && !allowed()) clear();
  };
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('wob:termly-consent-synced', consentChanged);

  if (!window.Shopify?.customerPrivacy) {
    try {
      window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
    } catch (error) {
      // History stays off until the privacy API loads.
    }
  }

  if (!read(window.sessionStorage, CART_KEY).length) rememberCart(cartProductIdsFromDom());

  window.WobSessionHistory = { list, record, cartProductIds, clear };
})();
